import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import googleCalendarService from '../../services/googleCalendarService';

/**
 * Google Calendar Connection Component
 * Allows professionals to connect/disconnect their Google Calendar
 * for automatic Google Meet link generation
 */
const GoogleCalendarConnect = () => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Check for OAuth callback success/error in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuthSuccess = urlParams.get('googleAuthSuccess');
    const googleAuthError = urlParams.get('googleAuthError');

    if (googleAuthSuccess === 'true') {
      setSuccess('Google Calendar connected successfully!');
      setConnected(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (googleAuthError) {
      setError(`Failed to connect Google Calendar: ${decodeURIComponent(googleAuthError)}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkConnectionStatus = async () => {
    try {
      setLoading(true);
      const status = await googleCalendarService.getConnectionStatus();
      setConnected(status.connected);
    } catch (err) {
      // Fail silently - assume not connected
      // This handles cases where user has never connected or auth issues
      console.error('Error checking Google Calendar status:', err);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);

      await googleCalendarService.connectGoogleCalendar();

      setSuccess('Google Calendar connected successfully!');
      setConnected(true);
    } catch (err) {
      console.error('Error connecting Google Calendar:', err);
      setError(err.message || 'Failed to connect Google Calendar');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await googleCalendarService.disconnectCalendar();

      setSuccess('Google Calendar disconnected successfully');
      setConnected(false);
      setDisconnectDialogOpen(false);
    } catch (err) {
      console.error('Error disconnecting Google Calendar:', err);
      setError(err.message || 'Failed to disconnect Google Calendar');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !connecting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-6">
          {/* Connection Status */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              {connected ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Connected"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<CancelIcon />}
                  label="Not Connected"
                  color="default"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Info Text */}
          <Alert severity="info" sx={{ mb: 2 }}>
            {connected ? (
              <>
                Your Google Calendar is connected. Google Meet links will be automatically generated
                for all virtual coffee chat sessions.
              </>
            ) : (
              <>
                Connect your Google Calendar to automatically generate Google Meet links for virtual
                sessions. This allows your clients to join meetings directly from their calendar.
              </>
            )}
          </Alert>

          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Benefits List */}
          {!connected && (
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Benefits:
              </Typography>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Automatic Google Meet link generation
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Calendar invites sent to both parties
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Automatic reminders before sessions
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    No app downloads required for attendees
                  </Typography>
                </li>
              </ul>
            </Box>
          )}

          {/* Action Buttons */}
          <Box display="flex" gap={2}>
            {connected ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setDisconnectDialogOpen(true)}
                disabled={loading}
              >
                Disconnect Calendar
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={connecting ? <CircularProgress size={20} /> : <LinkIcon />}
                onClick={handleConnect}
                disabled={connecting}
              >
                {connecting ? 'Connecting...' : 'Connect Google Calendar'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
      >
        <DialogTitle>Disconnect Google Calendar?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect your Google Calendar? Google Meet links will no
            longer be generated automatically for new sessions.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Existing calendar events and Meet links will not be affected.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoogleCalendarConnect;
